import { useCallback, useEffect, useState } from 'react';
import {
    HubConnection,
    HubConnectionBuilder,
    HubConnectionState,
    ILogger,
    IRetryPolicy,
    LogLevel,
} from '@microsoft/signalr';
import { useAuth } from '../auth/useAuth';
import { useAxiosSwagger } from './useAxiosSwagger';
import {
    ChecklistDeploy,
    ChecklistOverview,
    getRolloutChecklist,
    ROLLOUT_CHECKLIST_METHOD,
    ROLLOUT_CHECKLIST_OVERVIEW_METHOD,
    getEnvironmentWatches,
    getRolloutOverview,
    ROLLOUT_WATCH_METHOD,
    ROLLOUT_WATCHES_METHOD,
    rolloutHubUrl,
    RolloutEnvironmentRow,
    RolloutWatchSummary,
    RolloutWatchView,
} from '../api/rollout';

const ROW_METHOD = 'rolloutRow';

const quietLogger: ILogger = {
    log(logLevel, message) {
        if (
            logLevel >= LogLevel.Warning &&
            !message.includes('stopped during negotiation')
        ) {
            console.warn(`[rollout-hub] ${message}`);
        }
    },
};

/**
 * A rollout page stays open for a day: after a sleeping laptop or a restarted API it should
 * connect again, not give up after the default four tries and keep showing old readings.
 */
const keepReconnecting: IRetryPolicy = {
    nextRetryDelayInMilliseconds: ({ previousRetryCount }) =>
        [0, 2000, 10000][previousRetryCount] ?? 30000,
};

/**
 * One connection to the rollout hub for as long as a page needs it. `onReady` runs after every
 * (re)connect, so a page asks again for what it follows.
 */
const useRolloutHub = (
    setup: (hub: HubConnection) => void,
    onReady: (hub: HubConnection) => Promise<void> | void,
    deps: unknown[]
) => {
    const { getAccessTokenSilently } = useAuth();
    const [live, setLive] = useState(false);

    useEffect(() => {
        let cancelled = false;

        const hub = new HubConnectionBuilder()
            .withUrl(rolloutHubUrl, {
                accessTokenFactory: () => getAccessTokenSilently(),
            })
            .withAutomaticReconnect(keepReconnecting)
            .configureLogging(quietLogger)
            .build();

        setup(hub);

        const ready = () => {
            if (cancelled) return;

            setLive(true);
            Promise.resolve(onReady(hub)).catch(() => undefined);
        };

        hub.onreconnected(ready);
        hub.onreconnecting(() => !cancelled && setLive(false));
        hub.onclose(() => !cancelled && setLive(false));
        hub.start()
            .then(ready)
            .catch(() => !cancelled && setLive(false));

        return () => {
            cancelled = true;

            if (hub.state !== HubConnectionState.Disconnected)
                hub.stop().catch(() => undefined);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, deps);

    return live;
};

/** Every environment with its latest watch: loaded once, then kept up to date by the server. */
export const useRolloutOverview = () => {
    const axios = useAxiosSwagger();
    const [rows, setRows] = useState<RolloutEnvironmentRow[]>();
    const [error, setError] = useState<string>();

    const load = useCallback(async () => {
        if (!axios) return;

        try {
            setRows(await getRolloutOverview(axios));
            setError(undefined);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }, [axios]);

    useEffect(() => {
        load();
    }, [load]);

    const live = useRolloutHub(
        (hub) => {
            hub.on(ROW_METHOD, (row: RolloutEnvironmentRow) =>
                setRows((current) =>
                    current?.some(
                        (r) =>
                            r.customer === row.customer &&
                            r.environment === row.environment
                    )
                        ? current.map((r) =>
                              r.customer === row.customer &&
                              r.environment === row.environment
                                  ? row
                                  : r
                          )
                        : [...(current ?? []), row]
                )
            );
            // A watch started or ended somewhere: the list itself changed.
            hub.on(ROLLOUT_WATCHES_METHOD, () => load());
        },
        (hub) => hub.invoke('follow', null),
        [load]
    );

    return { rows, live, error, reload: load };
};

/** The watches of one environment, newest first; refreshed when the server says the list changed. */
export const useEnvironmentWatches = (
    customer: string,
    environment: string
) => {
    const axios = useAxiosSwagger();
    const [watches, setWatches] = useState<RolloutWatchSummary[]>();

    const load = useCallback(async () => {
        if (!axios) return;

        try {
            const list = await getEnvironmentWatches(
                axios,
                customer,
                environment
            );
            setWatches(
                [...list].sort(
                    (a, b) =>
                        Date.parse(b.deployedAt) - Date.parse(a.deployedAt)
                )
            );
        } catch {
            setWatches([]);
        }
    }, [axios, customer, environment]);

    useEffect(() => {
        load();
    }, [load]);

    useRolloutHub(
        (hub) => hub.on(ROLLOUT_WATCHES_METHOD, () => load()),
        (hub) => hub.invoke('follow', null),
        [load]
    );

    return { watches, reload: load };
};

/** One watch in full, pushed by the server as it reads the environment. */
export const useRolloutWatch = (watchId?: string) => {
    const [view, setView] = useState<RolloutWatchView>();

    useEffect(() => setView(undefined), [watchId]);

    const live = useRolloutHub(
        (hub) =>
            hub.on(
                ROLLOUT_WATCH_METHOD,
                (next: RolloutWatchView) => next.id === watchId && setView(next)
            ),
        async (hub) => {
            const first = await hub.invoke<RolloutWatchView | null>(
                'follow',
                watchId ?? null
            );

            if (first) setView(first);
        },
        [watchId]
    );

    return { view, live };
};

/**
 * The rollout checklist: every deploy, and the one that is open measured live. The server measures the
 * open deploy every few seconds and pushes every deploy a tick or a measurement changed, so a tick made
 * by Claude through the c2e-ops MCP shows here at once.
 */
export const useRolloutChecklist = (deployId?: string) => {
    const axios = useAxiosSwagger();
    const [overview, setOverview] = useState<ChecklistOverview>();
    const [error, setError] = useState<string>();

    const load = useCallback(async () => {
        if (!axios) return;

        try {
            setOverview(await getRolloutChecklist(axios));
            setError(undefined);
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e));
        }
    }, [axios]);

    useEffect(() => {
        load();
    }, [load]);

    /** One deploy as the server last sent it, in place in the overview. */
    const apply = useCallback(
        (deploy: ChecklistDeploy) =>
            setOverview((current) =>
                current
                    ? {
                          ...current,
                          deploys: current.deploys.map((d) =>
                              d.id === deploy.id ? deploy : d
                          ),
                      }
                    : current
            ),
        []
    );

    // Without a deploy in the address the page shows the next one: follow that, or the server never
    // measures it again and a tick made elsewhere only shows after a reload.
    const followed = deployId ?? overview?.next ?? null;

    const live = useRolloutHub(
        (hub) => {
            hub.on(ROLLOUT_CHECKLIST_METHOD, apply);
            hub.on(ROLLOUT_CHECKLIST_OVERVIEW_METHOD, () => load());
        },
        async (hub) => {
            const first = await hub.invoke<ChecklistDeploy | null>(
                'followChecklist',
                followed
            );

            if (first) apply(first);
        },
        [followed, apply, load]
    );

    return { overview, live, error, apply, reload: load };
};
